import { useRef, useEffect, useState, useMemo, Suspense } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import * as THREE from "three";
import { useTranslation } from "react-i18next";
import { useLang } from "../LanguageContext";
import { useCallback } from "react";

gsap.registerPlugin(ScrollTrigger);

// Identical across languages — not translated content.
const PILLAR_META = [
  { id: "identity", number: "01", lightColor: "#B5D3E5" },
  { id: "privacy", number: "02", lightColor: "#A5C0CE" },
  { id: "connection", number: "03", lightColor: "#E6F3FC" },
  { id: "audit", number: "04", lightColor: "#B5D3E5" },
];

// ========================
// Timeline — single source of truth
// ========================
const T = {
  SHATTER_END: 0.16,     // logo cracks apart, fragments stay close (subtle)
  ASSEMBLE_END: 0.38,    // fragments fly in and settle onto vault surface
  HOLD_END: 0.46,        // fragments linger on the vault before dissolving
  FADE_END: 0.41,        // fragments fully gone — vault stands alone, unlocked
  PILLAR_1_AT: 0.48,     // pillar 1 entry = lock slides into place (first time)
};

// Names of the four dial nodes inside the vault model, in the order
// they get set as pillars 2, 3, 4, and final scroll past.
const DIAL_NODES = ["ClockFace1_3", "ClockFace2_4", "ClockFace3_5", "ClockFace4_6", "ClockFace5_2"];

// ========================
// Shared scroll state — mutated directly, read inside the r3f loop
// without causing React re-renders.
// ========================
const scrollState = {
  progress: 0,
  lightColor: "#B5D3E5",
  vaultOpacity: 0,
  finalGlow: 0, // 0 -> 1, drives the closing "scan confirmation" effect
};

// ========================
// Logo — Hero + Subtle Shatter + Assemble into Vault
// ========================

function LogoFragments({ visible }) {
  const { scene } = useGLTF("/models/KMCLogo.glb");
  const { scene: vaultScene } = useGLTF("/models/combination_lock.glb");
  const groupRef = useRef();
  const [fragments, setFragments] = useState([]);

  useEffect(() => {
    let sourceMesh = null;
    scene.traverse((child) => {
      if (child.isMesh) sourceMesh = child;
    });
    if (!sourceMesh) return;

    // Sample target points across the vault's surface, in the same
    // local space the vault group will render in.
    vaultScene.updateMatrixWorld(true);
    const vaultPoints = [];
    const vaultMatrix = new THREE.Matrix4();
    vaultScene.traverse((child) => {
      if (!child.isMesh) return;
      const pos = child.geometry.attributes.position;
      for (let i = 0; i < pos.count; i += 20) {
        const v = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
        vaultScene.updateMatrixWorld(true);
        vaultMatrix.copy(vaultScene.matrixWorld);
        v.applyMatrix4(child.matrixWorld);
        v.applyEuler(new THREE.Euler(0, -Math.PI / 2, 0));
        v.add(new THREE.Vector3(-2, -1.4, 0));
        vaultPoints.push(v);
      }
    });

    const geom = sourceMesh.geometry;
    const posAttr = geom.attributes.position;
    const indices = geom.index ? geom.index.array : null;
    const faceCount = indices ? indices.length / 3 : posAttr.count / 3;

    const isMobile = window.innerWidth < 768;
    const FRAGMENT_COUNT = isMobile ? 12 : 30;
    const facesPerFragment = Math.max(1, Math.floor(faceCount / FRAGMENT_COUNT));

    const built = [];

    for (let f = 0; f < FRAGMENT_COUNT; f++) {
      const startFace = f * facesPerFragment;
      const endFace = Math.min(startFace + facesPerFragment, faceCount);
      if (startFace >= faceCount) break;

      const positions = [];
      for (let fi = startFace; fi < endFace; fi++) {
        for (let v = 0; v < 3; v++) {
          const idx = indices ? indices[fi * 3 + v] : fi * 3 + v;
          positions.push(posAttr.getX(idx), posAttr.getY(idx), posAttr.getZ(idx));
        }
      }
      if (positions.length === 0) continue;

      const newGeom = new THREE.BufferGeometry();
      newGeom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
      newGeom.scale(8,8,8);
      newGeom.computeVertexNormals();

      const dir = new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2
      ).normalize();

      const center = new THREE.Vector3(-2, -1.4, 0);

const spread = new THREE.Vector3(
  (Math.random() - 0.5) * 0.8,
  (Math.random() - 0.5) * 0.8,
  (Math.random() - 0.5) * 0.3
);

built.push({
  geometry: newGeom,

  startPos: dir.clone().multiplyScalar(0.35),

  targetPos: center.clone().add(spread).add(new THREE.Vector3(-3, 1.8, -1.35)),

  rot: new THREE.Vector3(
    (Math.random() - 0.5) * 1.5,
    (Math.random() - 0.5) * 1.5,
    (Math.random() - 0.5) * 1.5
  ),
});
    }

    setFragments(built);
  }, [scene, vaultScene]);

  const origin = new THREE.Vector3();

  useFrame(({ clock }) => {
    if (!groupRef.current || fragments.length === 0) return;

    const progress = scrollState.progress;
    
    if (window.innerWidth < 768 && progress > T.FADE_END) {
  groupRef.current.visible = false;
  return;
}

    if (progress < 0.01) {
      groupRef.current.rotation.y = 0;
    }

    groupRef.current.children.forEach((mesh, i) => {
      const frag = fragments[i];
      if (!frag) return;

      // Phase 1 — gentle crack outward (small radius, not explosive)
      if (progress < T.SHATTER_END) {
        const tt = progress / T.SHATTER_END;
        mesh.position.lerpVectors(
        origin,
        frag.startPos,
        tt
        )
        mesh.rotation.x = frag.rot.x * tt;
        mesh.rotation.y = frag.rot.y * tt;
        mesh.rotation.z = frag.rot.z * tt;
        mesh.material.opacity = 1;
      }
      // Phase 2 — fly outward to the vault surface, settling rotation
      else if (progress < T.ASSEMBLE_END) {
        const tt = (progress - T.SHATTER_END) / (T.ASSEMBLE_END - T.SHATTER_END);
        const eased = 1 - Math.pow(1 - tt, 3);
        mesh.position.lerpVectors(frag.startPos, frag.targetPos, eased);
        mesh.rotation.x *= 0.94;
        mesh.rotation.y *= 0.94;
        mesh.rotation.z *= 0.94;
        mesh.material.opacity = 1;
      }
      // Phase 3 — hold in place on the vault surface (visually "assembled")
      else if (progress < T.HOLD_END) {
        mesh.position.copy(frag.targetPos);
        mesh.material.opacity = 1;
      }
      // Phase 4 — dissolve slowly, vault stands alone underneath
      else {
        mesh.position.copy(frag.targetPos);
        const fadeT = THREE.MathUtils.clamp(
          (progress - T.HOLD_END) / (T.FADE_END - T.HOLD_END),
          0,
          1
        );
        mesh.material.opacity = 1 - fadeT;
      }
    });
  });

  if (fragments.length === 0) return null;

  return (
    <group ref={groupRef} visible={visible} scale={1} position = {[2.5,1,1]} rotation={[-Math.PI / 2, 0, -0.3]}>
      {fragments.map((frag, i) => (
        <mesh key={i} geometry={frag.geometry}>
          <meshStandardMaterial
            color="#B5D3E5"
            emissive="#B5D3E5"
            emissiveIntensity={0.6}
            metalness={0.6}
            roughness={0.25}
            transparent
            opacity={1}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
}

// ========================
// Vault Model
// ========================

function VaultModel({ onReady }) {
  const { scene } = useGLTF("/models/combination_lock.glb");
  const materialsRef = useRef([]);
  const glowRef = useRef(0);

  useEffect(() => {
    const mats = [];

    scene.traverse((child) => {
      if (child.isMesh) {
        child.rotation.set(0, 0, 0);

        const mat = child.material.clone();
        mat.transparent = true;
        mat.opacity = 0;

        mat.color = new THREE.Color("#C8D6E5");
        mat.emissive = new THREE.Color("#6FA8C9");
        mat.emissiveIntensity = 0.25;

        if ("metalness" in mat) mat.metalness = 0.45;
        if ("roughness" in mat) mat.roughness = 0.35;

        child.material = mat;
        mats.push(mat);
      }
    });

    materialsRef.current = mats;
    if (onReady) onReady(scene);
  }, [scene, onReady]);

  useFrame(() => {
    // Vault fades in as fragments arrive, stays fully opaque after
    const opacity = THREE.MathUtils.clamp(
      (scrollState.progress - T.SHATTER_END) / (T.ASSEMBLE_END - T.SHATTER_END),
      0,
      1
    );
    scrollState.vaultOpacity = opacity;

    // Final confirmation glow pulses emissive intensity once at the end
    const targetGlow = scrollState.finalGlow;
    glowRef.current += (targetGlow - glowRef.current) * 0.08;

    materialsRef.current.forEach((mat) => {
      mat.opacity = opacity;
      mat.emissiveIntensity = 0.25 + glowRef.current * 0.9;
    });
  });

  return (
    <group scale={1} position={[-2, -1.4, 0]} rotation={[0, -Math.PI / 2, 0]}>
      <primitive object={scene} />
    </group>
  );
}

// ========================
// Scan Line 
// ========================

function ScanLine() {
  const meshRef = useRef();
  const materialRef = useRef();

  const startY = 2.8;
  const endY = -2.8;

  const scanProgress = useRef(0);

  useFrame(() => {
    if (!meshRef.current || !materialRef.current) return;

    const glow = scrollState.finalGlow;

    // smooth delay
    scanProgress.current += 
      (glow - scanProgress.current) * 0.01;

    materialRef.current.opacity = scanProgress.current * 0.8;

    meshRef.current.position.y =
      startY + scanProgress.current * (endY - startY);
  });

  return (
    <mesh 
      ref={meshRef} 
      position={[0, startY, 0.5]}
      rotation={[0,0,0]}
    >
      <planeGeometry args={[1.6,0.04]} />

      <meshBasicMaterial
        ref={materialRef}
        color="#E6F3FC"
        transparent
        opacity={0}
        depthWrite={false}
        side={THREE.DoubleSide}
      />

    </mesh>
  );
}

// ========================
// Camera + Lighting
// ========================

function CameraRig() {
  const { camera } = useThree();
  const current = useRef(new THREE.Vector3(0, 0, 7));

  useFrame(() => {
    const p = scrollState.progress;
    let target;

    if (p < 0.3) {
      const t = p / 0.3;
      target = new THREE.Vector3(0, THREE.MathUtils.lerp(0, 0.4, t), THREE.MathUtils.lerp(7, 6, t));
    } else {
      const t = (p - 0.3) / 0.7;
      target = new THREE.Vector3(
        Math.sin(t * Math.PI * 0.5) * 1.4,
        0.6,
        6 - t * 1.0
      );
    }

    current.current.lerp(target, 0.04);
    camera.position.copy(current.current);
    camera.lookAt(0, 0, 0);
  });

  return null;
}

function DynamicLight() {
  const lightRef = useRef();
  const currentColor = useRef(new THREE.Color("#B5D3E5"));

  useFrame(() => {
    // Once the final glow kicks in, settle the light to a single calm
    // cool tone instead of following the per-pillar color.
    const baseTarget = scrollState.finalGlow > 0.5
      ? "#B5D3E5"
      : scrollState.lightColor;

    const target = new THREE.Color(baseTarget);
    currentColor.current.lerp(target, 0.05);
    if (lightRef.current) lightRef.current.color.copy(currentColor.current);
  });

  return <pointLight ref={lightRef} position={[3, 5, 5]} intensity={4} distance={25} />;
}

// ========================
// Scene
// ========================

function Scene({ onVaultReady }) {
  return (
    <>
      <ambientLight intensity={0.6} />
      <hemisphereLight skyColor="#B5D3E5" groundColor="#091B3A" intensity={0.5} />
      <DynamicLight />
      <pointLight position={[-4, 2, 3]} intensity={1.5} color="#E6F3FC" />
      <pointLight position={[0, -3, 4]} intensity={1} color="#A5C0CE" />

      <CameraRig />

      <LogoFragments visible={scrollState.progress < T.FADE_END + 0.05} />

      <VaultModel onReady={onVaultReady} />

      <group 
  position={[-2,-1.4,0]}
>
    <ScanLine />

</group>
    
    </>
  );
}

// ========================
// Main Component
// ========================

export default function DataSecurity() {
  const { lang } = useLang();
  const { t } = useTranslation();
  // Memoized so `tx.pillars` keeps a stable reference across renders that
  // don't change language — the per-pillar ScrollTrigger effect below
  // depends on it, and recreating that array every render (e.g. on every
  // scroll-driven state update) tore down and rebuilt the ScrollTriggers
  // on every frame, breaking the lock/dial enter-crossing detection.
  const tx = useMemo(
    () => ({
      tag: t("dataSecurity.tag"),
      title: t("dataSecurity.title"),
      pillars: t("dataSecurity.pillars", { returnObjects: true }).map((pillar, i) => ({
        ...pillar,
        ...PILLAR_META[i],
      })),
      pdpa: t("dataSecurity.pdpa"),
      pdpa_desc: t("dataSecurity.pdpaDesc"),
      finalTitle: t("dataSecurity.finalTitle"),
    }),
    [t]
  );

  const sectionRef = useRef(null);
  const panelRefs = useRef([]);
  const titleRef = useRef(null);
  const finalRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(-1);
  const [heroOpacity, setHeroOpacity] = useState(1);
  const [showHint, setShowHint] = useState(true);
  const [showFinalBadge, setShowFinalBadge] = useState(false);

  const clockFaceNodes = useRef({});
  const lockNode = useRef(null);
  const lockLockedPos = useRef(null);   // native .glb position = locked pose
  const lockUnlockedPos = useRef(null); // lifted position = unlocked pose
  const lockInitialRot = useRef(null);
  const hasLockedFirstRef = useRef(false);
  const hasFinalizedRef = useRef(false);
  const dialTurnedRef = useRef([false, false, false, false, false]);

 const handleVaultReady = useCallback((scene) => {
  clockFaceNodes.current = {};

  console.count("handleVaultReady");
  

  scene.traverse((child) => {
    if (child.name?.startsWith("ClockFace")) {
      clockFaceNodes.current[child.name] = child;
    }

    if (child.name === "Lock_1") {
      lockNode.current = child;

      lockInitialRot.current = child.rotation.clone();
      lockLockedPos.current = child.position.clone();

      lockUnlockedPos.current =
        child.position.clone().add(new THREE.Vector3(0,1.1,0));

      child.position.copy(lockUnlockedPos.current);
    }
  });

}, []);

  // Master scroll progress
  useEffect(() => {
    
  const section = sectionRef.current;

  scrollState.progress = 0;
  scrollState.lightColor = "#B5D3E5";
  scrollState.vaultOpacity = 0;
  scrollState.finalGlow = 0;

  setActiveIndex(-1);
  setHeroOpacity(1);
  setShowHint(true);
  setShowFinalBadge(false);

    if (!section) return;

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: section,
        start: "top top",
        end: "bottom bottom",
        scrub: 1,
        onUpdate: (self) => {
          const p = self.progress;
          scrollState.progress = p;

          setHeroOpacity(p < 0.05 ? 1 : Math.max(0, 1 - p / 0.16));
          setShowHint(p < 0.05);
        },
      });
    }, section);

    return () => ctx.revert();
  }, []);

  // Per-pillar triggers
  useEffect(() => {
    const ctx = gsap.context(() => {
      panelRefs.current.forEach((panel, i) => {
        if (!panel) return;
        const pillar = tx.pillars[i];

        ScrollTrigger.create({
          trigger: panel,
          start: "top 60%",
          end: "bottom 40%",
          onEnter: () => {

            setActiveIndex(i);
            scrollState.lightColor = pillar.lightColor;

            if (i === 0) {
              // Pillar 1 — lock slides into place for the first time
              if (!hasLockedFirstRef.current) {
                hasLockedFirstRef.current = true;
                lockIntoPlace();
              }
            } else {
              // Pillars 2-4 — turn the corresponding dial
              const dialIndex = i - 1;
              if (!dialTurnedRef.current[dialIndex]) {
                dialTurnedRef.current[dialIndex] = true;
                turnDial(DIAL_NODES[dialIndex]);
              }
            }
          },
          onEnterBack: () => {
            setActiveIndex(i);
            scrollState.lightColor = pillar.lightColor;
          },
          onLeaveBack: () => {

          if (i === 0) {

            unlockLock();

            hasLockedFirstRef.current = false;

          } else {

            const dialIndex = i - 1;

            resetDial(DIAL_NODES[dialIndex]);

            dialTurnedRef.current[dialIndex] = false;
          }
        }
        });
      });

      // Final panel — last dial turns + scan confirmation effect
      if (finalRef.current) {
        ScrollTrigger.create({
          trigger: finalRef.current,
          start: "top 70%",
          onEnter: () => {
            if (!hasFinalizedRef.current) {
              hasFinalizedRef.current = true;

              if (!dialTurnedRef.current[4]) {
                dialTurnedRef.current[4] = true;
                turnDial(DIAL_NODES[4]);
              }

              // Slight delay so the last dial click is felt before the
              // confirmation glow washes over the vault.
              gsap.delayedCall(0.5, playFinalConfirmation);
            }
          },
          onLeaveBack() {

          reverseFinalConfirmation();

          resetDial(DIAL_NODES[3]);

          hasFinalizedRef.current = false;

          dialTurnedRef.current[3] = false;

        }
        });
      }
    });

    return () => ctx.revert();
  }, [lang, tx.pillars]);

  // Step-like dial turn with randomized direction and duration per click —
  // gives each dial its own organic feel rather than identical motion.
  const turnDial = (nodeName) => {
    const node = clockFaceNodes.current[nodeName];
    if (!node) return;

    const steps = 3 + Math.floor(Math.random() * 2); // 3-4 clicks
    const tl = gsap.timeline();

    for (let s = 0; s < steps; s++) {
      const direction = Math.random() > 0.5 ? 1 : -1;
      const angle = (Math.PI * (0.3 + Math.random() * 0.5)) * direction;
      const duration = 0.16 + Math.random() * 0.14;

      tl.to(node.rotation, {
        z: node.rotation.z + angle,
        duration,
        ease: s === steps - 1 ? "back.out(2.5)" : "power1.out",
      }).to(node.rotation, { duration: 0.04 + Math.random() * 0.05 });
    }
  };

const lockIntoPlace = () => {
  const lock = lockNode.current;
  if (!lock || !lockLockedPos.current) return;

  gsap.to(lock.position, {
    x: lockLockedPos.current.x,
    y: lockLockedPos.current.y,
    z: lockLockedPos.current.z,
    duration: 1,
    ease: "power3.inOut",
  });

   gsap.to(lock.rotation,{
   x:lockInitialRot.current.x,
   y:lockInitialRot.current.y,
   z:lockInitialRot.current.z,
   duration:1
 });
};

const unlockLock = () => {
  const lock = lockNode.current;

  if (!lock || !lockUnlockedPos.current) return;

  gsap.to(lock.position, {
    x: lockUnlockedPos.current.x,
    y: lockUnlockedPos.current.y,
    z: lockUnlockedPos.current.z,
    duration: 1,
    ease: "power3.inOut",
  });

  gsap.to(lock.rotation, {
    x: lockInitialRot.current.x,
    y: lockInitialRot.current.y,
    z: lockInitialRot.current.z,
    duration: 1,
  });
};

const resetDial = (nodeName) => {
  const node = clockFaceNodes.current[nodeName];
  if (!node) return;

  gsap.to(node.rotation, {
    z: 0,
    duration: 0.8,
    ease: "power2.out",
  });
};

const reverseFinalConfirmation = () => {
  gsap.to(scrollState, {
    finalGlow: 0,
    duration: 0.8,
    ease: "power2.out",
  });

  setShowFinalBadge(false);
};

  // Final — scan line sweep + emissive pulse + checkmark fade-in
  const playFinalConfirmation = () => {
    gsap.to(scrollState, {
      finalGlow: 1,
      duration: 1.6,
      ease: "power2.out",
      onComplete: () => setShowFinalBadge(true),
    });
  };

  useEffect(() => {
    if (activeIndex < 0) return;
    const panel = panelRefs.current[activeIndex];
    if (!panel) return;
    const contentEl = panel.querySelector(".ds3d__panel-content");
    if (!contentEl) return;

    gsap.fromTo(
      contentEl,
      { opacity: 0, x: 30 },
      { opacity: 1, x: 0, duration: 0.6, ease: "power3.out" }
    );
  }, [activeIndex]);

  useEffect(() => {
    if (!titleRef.current) return;
    gsap.fromTo(
      titleRef.current,
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 1, ease: "power3.out", delay: 0.3 }
    );
  }, []);

  return (
    <section ref={sectionRef} className="ds3d">

      <div className="ds3d__canvas-wrap">
        <Canvas
          camera={{ position: [0, 0, 7], fov: 50 }}
          gl={{ antialias: true, alpha: true }}
          dpr={[1, 2]}
        >
          <Suspense fallback={null}>
            <Scene onVaultReady={handleVaultReady} />
          </Suspense>
        </Canvas>

        <div className="ds3d__vignette" />

        <div ref={titleRef} className="ds3d__hero" style={{ opacity: heroOpacity }}>
          <p className="ds3d__tag">{tx.tag}</p>
          <h2 className="ds3d__title">
            {tx.title.split("\n").map((line, i) => (
              <span key={i}>{line}<br /></span>
            ))}
          </h2>
        </div>

        {showHint && (
          <div className="ds3d__hint">
            <div className="ds3d__hint-line" />
            <span>scroll</span>
          </div>
        )}

      </div>

      <div className="ds3d__shatter-spacer" />

      <div className="ds3d__panels">

        {tx.pillars.map((pillar, i) => (
          <div
            key={pillar.id}
            ref={(el) => (panelRefs.current[i] = el)}
            className={`ds3d__panel ${i === activeIndex ? "ds3d__panel--active" : ""}`}
          >
            <div className="ds3d__panel-content">
              <span className="ds3d__panel-num">{pillar.number}</span>
              <h3 className="ds3d__panel-title">
                {pillar.title.split("\n").map((line, j) => (
                  <span key={j}>{line}<br /></span>
                ))}
              </h3>
              <p className="ds3d__panel-desc">{pillar.desc}</p>
              <div className="ds3d__panel-tags">
                {pillar.trust.map((tag, j) => (
                  <span key={j} className="ds3d__panel-tag">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        ))}

        <div ref={finalRef} className="ds3d__panel ds3d__panel--final">
          <div className="ds3d__panel-content">
            <h3 className={`ds3d__final-title ${showFinalBadge ? "ds3d__final-title--visible" : ""}`}>
              <span className="ds3d__final-check">✓</span>
              {tx.finalTitle}
            </h3>
            <div className="ds3d__pdpa">
              <span className="ds3d__pdpa-check">✓</span>
              <div>
                <p className="ds3d__pdpa-badge">{tx.pdpa}</p>
                <p className="ds3d__pdpa-desc">{tx.pdpa_desc}</p>
              </div>
            </div>
          </div>
        </div>

      </div>

    </section>
  );
}

useGLTF.preload("/models/KMCLogo.glb");
useGLTF.preload("/models/combination_lock.glb");